// PushNotifications.js — Friendly push notification permission + management
import React, { useState, useEffect, useCallback } from 'react';

const VAPID_PUBLIC_KEY = 'BG-u8xhYBDxiVckbSb84uhfJvU7FJk-52la5EJ9QT4Cudvt1KbIvnPADAemxzYHHIYtvPhzIgZz8Vff1jZsJVmk';

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  return Uint8Array.from([...raw].map(c => c.charCodeAt(0)));
}

// ── Permission prompt modal ─────────────────────────────────────
export function PushPermissionPrompt({ profile, user, onDismiss }) {
  const [state, setState] = useState('asking'); // asking | subscribing | done | error

  const subscribe = async () => {
    setState('subscribing');
    try {
      if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
        setState('error'); return;
      }
      const reg = await navigator.serviceWorker.ready;
      const perm = await Notification.requestPermission();
      if (perm !== 'granted') { setState('error'); onDismiss('denied'); return; }

      const existing = await reg.pushManager.getSubscription();
      const sub = existing || await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
      });

      await fetch('/api/save-subscription', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ subscription: sub, email: user?.email || profile?.parent_email }),
      });

      // Save pref locally
      localStorage.setItem('ns_push_granted', '1');
      localStorage.setItem('ns_push_asked', Date.now().toString());
      setState('done');
      setTimeout(() => onDismiss('granted'), 1800);
    } catch {
      setState('error');
      setTimeout(() => onDismiss('error'), 1500);
    }
  };

  const later = () => {
    localStorage.setItem('ns_push_asked', Date.now().toString());
    onDismiss('later');
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9500,
      background: 'rgba(0,0,0,0.55)',
      display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
      fontFamily: "'Nunito', sans-serif",
    }}>
      <div style={{
        background: 'white', borderRadius: '24px 24px 0 0',
        padding: '24px 20px 32px', width: '100%', maxWidth: 430,
        boxShadow: '0 -8px 32px rgba(0,0,0,0.15)',
      }}>
        {state === 'done' ? (
          <div style={{ textAlign: 'center', padding: '8px 0' }}>
            <p style={{ fontSize: 48, margin: '0 0 12px' }}>🎉</p>
            <h3 style={{ fontSize: 20, fontWeight: 900, color: '#1A1A2E', margin: '0 0 6px' }}>
              You're all set!
            </h3>
            <p style={{ fontSize: 14, color: '#888', fontWeight: 700, margin: 0 }}>
              Your pet will message you when it misses you 💛
            </p>
          </div>
        ) : state === 'error' ? (
          <div style={{ textAlign: 'center', padding: '8px 0' }}>
            <p style={{ fontSize: 48, margin: '0 0 12px' }}>😔</p>
            <p style={{ fontSize: 15, fontWeight: 700, color: '#888', margin: 0 }}>
              Notifications blocked — you can enable them in browser settings anytime.
            </p>
          </div>
        ) : (
          <>
            {/* Lucky illustration */}
            <div style={{ textAlign: 'center', marginBottom: 16 }}>
              <div style={{ fontSize: 64, margin: '0 auto 8px' }}>⭐</div>
              <h3 style={{ fontSize: 20, fontWeight: 900, color: '#1A1A2E', margin: '0 0 8px' }}>
                Your pet misses you!
              </h3>
              <p style={{ fontSize: 14, color: '#666', fontWeight: 700, margin: '0 0 6px', lineHeight: 1.6 }}>
                Let your pet send you reminders. It gets lonely when you're away 💛
              </p>
            </div>

            {/* Notification examples */}
            <div style={{ marginBottom: 20 }}>
              {[
                { icon: '⭐', text: `"I've been waiting! Your streak is at risk 🔥"` },
                { icon: '🎯', text: `"I picked a special mission for you today!"` },
                { icon: '🌙', text: `"The moon was dimmer today... I think you know why"` },
                { icon: '🤖', text: `"COSMO MISSING YOU. Please return immediately 🚀"` },
              ].map(({ icon, text }) => (
                <div key={text} style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '8px 12px', marginBottom: 6,
                  background: '#F8F9FF', borderRadius: 10,
                }}>
                  <span style={{ fontSize: 18, flexShrink: 0 }}>{icon}</span>
                  <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: '#444' }}>{text}</p>
                </div>
              ))}
            </div>

            <button
              onClick={subscribe}
              disabled={state === 'subscribing'}
              style={{
                width: '100%', padding: '14px', borderRadius: 14, border: 'none',
                cursor: 'pointer', marginBottom: 10,
                background: state === 'subscribing' ? '#CCC' : 'linear-gradient(135deg,#0066FF,#8B2BE2)',
                color: 'white', fontWeight: 900, fontSize: 16,
                fontFamily: "'Nunito', sans-serif",
              }}
            >
              {state === 'subscribing' ? 'Setting up...' : '🔔 Yes, remind me!'}
            </button>
            <button onClick={later} style={{
              width: '100%', padding: '12px', borderRadius: 14,
              border: 'none', cursor: 'pointer',
              background: 'transparent', color: '#AAA',
              fontWeight: 700, fontSize: 14, fontFamily: "'Nunito', sans-serif",
            }}>Maybe later</button>
          </>
        )}
      </div>
    </div>
  );
}

// ── Hook: manage when to show the prompt ───────────────────────
export function usePushNotifications({ user, profile }) {
  const [showPrompt, setShowPrompt] = useState(false);

  useEffect(() => {
    if (!user || !profile) return;
    if (!('Notification' in window) || !('serviceWorker' in navigator)) return;
    if (Notification.permission === 'granted') return; // already subscribed
    if (localStorage.getItem('ns_push_granted')) return; // already set up

    const lastAsked = parseInt(localStorage.getItem('ns_push_asked') || '0', 10);
    const daysSince = (Date.now() - lastAsked) / (1000 * 60 * 60 * 24);

    // Show on first visit or after 3 days
    if (!lastAsked || daysSince >= 3) {
      // Delay 8s after login so it's not the first thing they see
      const t = setTimeout(() => setShowPrompt(true), 8000);
      return () => clearTimeout(t);
    }
  }, [user, profile]);

  const dismiss = useCallback((reason) => {
    setShowPrompt(false);
  }, []);

  return { showPrompt, dismiss };
}

// ── In-app notification toast ──────────────────────────────────
export function NotificationToast({ message, onClose }) {
  useEffect(() => {
    const t = setTimeout(onClose, 4000);
    return () => clearTimeout(t);
  }, [onClose]);

  if (!message) return null;
  return (
    <div style={{
      position: 'fixed', top: 16, left: '50%', transform: 'translateX(-50%)',
      zIndex: 9000, maxWidth: 360, width: 'calc(100% - 32px)',
      background: 'linear-gradient(135deg,#1A1A2E,#2A2A4A)',
      borderRadius: 14, padding: '12px 16px',
      display: 'flex', alignItems: 'center', gap: 10,
      boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
      fontFamily: "'Nunito', sans-serif",
      animation: 'slideDown 0.3s ease-out',
    }}>
      <style>{`@keyframes slideDown{from{opacity:0;transform:translateX(-50%) translateY(-20px)}to{opacity:1;transform:translateX(-50%) translateY(0)}}`}</style>
      <span style={{ fontSize: 20, flexShrink: 0 }}>{message.icon || '🔔'}</span>
      <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: 'white', flex: 1 }}>
        {message.text}
      </p>
      <button onClick={onClose} style={{
        background: 'none', border: 'none', color: 'rgba(255,255,255,0.5)',
        cursor: 'pointer', fontSize: 16, padding: 0, flexShrink: 0,
      }}>✕</button>
    </div>
  );
}

// ── postActivityNotification — call from anywhere in the app ───
export function triggerNotification({ user, profile, type, extra = {} }) {
  if (!user || !profile) return;

  const MESSAGES = {
    streak_risk: {
      icon: '🔥',
      text: `${profile.child_name}, your ${extra.streak}-day streak is at risk! Log in to save it.`,
    },
    friend_activity: {
      icon: '🌟',
      text: `Your friend ${extra.friendName} just ${extra.action}!`,
    },
    new_quiz: {
      icon: '🎮',
      text: 'A new community quiz just dropped in Creator Studio — be the first to play it!',
    },
    mystery_mission: {
      icon: '🎯',
      text: `${profile.child_name}, your Mystery Mission for today is ready!`,
    },
    streak_milestone: {
      icon: '🔥',
      text: `One more day and you unlock Future Innovator Chest! Don't stop now.`,
    },
    chest_ready: {
      icon: '📦',
      text: `You have a chest waiting to be opened in your city!`,
    },
  };

  const msg = MESSAGES[type];
  if (!msg) return null;
  return msg;
}
